// Projeção da ruptura na aba "Pedidos" — ciente da demanda.
//
// Simula o estoque de TODOS os produtos dia a dia ao longo de um horizonte curto
// (30–60 dias): cada dia DESCONTA a demanda diária e SOMA as chegadas dos pedidos
// a caminho (Pendente/Enviado) na data prevista. A cada dia conta quantos itens
// ficam em ruptura (régua única isRupturaAcionavel) e devolve o índice projetado.
//
// Responde: em que data a ruptura projetada zera (se zerar), qual o menor índice
// alcançado após as entregas (líquido da demanda) e onde ele assenta no fim do
// horizonte — capturando itens que voltam a zerar e itens que zeram pela demanda.
//
// Puro/determinístico (sem IA, sem rede) para ser testável e reutilizável entre
// desktop e mobile.

// Régua única de ruptura = saldo ≤ 0 E status fora de HIDES_ZERADO (ver
// isRupturaAcionavel em replenishment.ts). Aqui ela é decomposta: pulamos os
// HIDES_ZERADO uma vez por produto e testamos `stock <= 0` a cada dia simulado.
import { HIDES_ZERADO } from './replenishment'
import { resolveExpectedArrival } from './leadTime'
import type { KanbanStatusOp, PurchaseOrderWithItems } from '../types'

const DAY_MS = 86_400_000
/** Dias corridos por mês — converte a demanda mensal em demanda diária de calendário
 *  (a linha do tempo é em datas; o daily_avg/26 do app é base dia-útil). */
const CALENDAR_DAYS_PER_MONTH = 30

/** Meta de ruptura: ≤5% (faixa verde; a meta formal de compras é ≤7%). */
export const RUPTURA_TARGET = 0.05

export type RupturaBand = 'verde' | 'ambar' | 'vermelho'
/** Banda de severidade de um índice de ruptura (0–1), alinhada ao OperacionalPanel:
 *  ≤5% verde · ≤15% âmbar · >15% vermelho. */
export function rupturaBand(index: number): RupturaBand {
  if (index <= 0.05) return 'verde'
  if (index <= 0.15) return 'ambar'
  return 'vermelho'
}

/** Início do dia (00:00 local) em ms — comparações por data de calendário. */
function dayStart(ms: number): number {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** Entrada enxuta de produto — o que a simulação precisa. */
export interface RupturaForecastRow {
  id: string
  current_stock: number
  kanban_status: KanbanStatusOp
  /** Demanda mensal usada no desconto diário (= demanda_prevista). */
  monthly_demand: number
  /** Ponto de ressuprimento (gatilho R). Só usado no modo `replenish`. */
  reorder_point?: number
  /** Lead time do fornecedor em dias. Só usado no modo `replenish`. null = não reabastecível. */
  lead_time?: number | null
}

export interface RupturaProjectionPoint {
  /** Data do dia (ISO, início do dia). */
  date: string
  /** Offset em dias a partir de hoje (0 = hoje). */
  dayOffset: number
  /** Itens em ruptura projetados neste dia. */
  count: number
  /** Índice de ruptura (0–1) neste dia. */
  index: number
}

export interface RupturaForecast {
  totalSkus: number
  horizonDays: number
  /** Ruptura HOJE (dia 0) — bate com a "Taxa de ruptura" do Dashboard. */
  rupturaTotal: number
  currentIndex: number
  /** Menor índice projetado no horizonte = melhor resultado após as entregas, líquido da demanda. */
  minIndex: number
  minIndexDate: string
  minCount: number
  /** Índice no fim do horizonte (onde assenta). */
  endIndex: number
  endDate: string
  /** 1ª data em que o índice projetado chega a 0; null se não zera no horizonte. */
  eliminationDate: string | null
  /** Itens em ruptura com pedido a caminho mas SEM data resolvível (fora da curva). */
  semDataCount: number
  /** Série diária completa (0..horizonte). */
  series: RupturaProjectionPoint[]
  /** Série amostrada (semanal + último dia) para o gráfico. */
  chartPoints: (RupturaProjectionPoint & { label: string })[]
}

interface Input {
  rows: ReadonlyArray<RupturaForecastRow>
  orders: ReadonlyArray<PurchaseOrderWithItems>
  now?: number
  /** Override do horizonte (dias). Padrão: clamp(maxChegada + 7, 30, 60). */
  horizonDays?: number
  /**
   * Reposição contínua: quando um item reabastecível cruza o ponto de ressuprimento
   * e não há pedido em trânsito, agenda um novo pedido (política R,S) chegando após o
   * lead time — reflete o processo real de compra. Default `false` (single-shot:
   * só os pedidos/plano dados, sem recompra — usado em Pedidos e na baseline).
   */
  replenish?: boolean
}

/**
 * Data efetiva de chegada de um pedido a caminho (Pendente/Enviado), em ms no
 * início do dia. `null` quando indefinida (ex.: Pendente sem lead time).
 *  - Enviado: previsão real (estimated_arrival ou sent_at + lead time); se já
 *    venceu, chega "imediatamente" (hoje).
 *  - Pendente: ainda não saiu → estima hoje + lead time do fornecedor.
 */
function effectiveArrival(o: PurchaseOrderWithItems, now: number): number | null {
  const lead = o.suppliers?.lead_time_days ?? null
  if (o.status === 'Enviado') {
    const e = resolveExpectedArrival({
      status: o.status,
      estimated_arrival: o.estimated_arrival,
      received_at: o.received_at,
      sent_at: o.sent_at,
      lead_time_days: lead,
    })
    if (!e) return null
    return Math.max(dayStart(e.getTime()), dayStart(now))
  }
  if (o.status === 'Pendente') {
    if (lead == null) return null
    return dayStart(now) + lead * DAY_MS
  }
  return null // Recebido / Cancelado não são "a caminho"
}

const fmtLabel = (ms: number) =>
  new Date(ms).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })

export function forecastRupturaElimination({ rows, orders, now = Date.now(), horizonDays, replenish = false }: Input): RupturaForecast {
  const totalSkus = rows.length
  const today = dayStart(now)

  // 1. Chegadas por produto (offset de dia ≥ 1; dia 0 = realidade atual).
  const arrivalsByProduct = new Map<string, { day: number; qty: number }[]>()
  const semDataProducts = new Set<string>()
  let maxArrivalDay = 0
  for (const o of orders) {
    if (o.status !== 'Pendente' && o.status !== 'Enviado') continue
    const ms = effectiveArrival(o, now)
    for (const it of o.purchase_order_items) {
      if (!it.product_id || it.quantity <= 0) continue
      if (ms == null) { semDataProducts.add(it.product_id); continue }
      const day = Math.max(1, Math.round((ms - today) / DAY_MS))
      if (day > maxArrivalDay) maxArrivalDay = day
      const list = arrivalsByProduct.get(it.product_id) ?? []
      list.push({ day, qty: it.quantity })
      arrivalsByProduct.set(it.product_id, list)
    }
  }

  // 2. Horizonte: janela curta de 30–60 dias que cobre a última entrega + cauda.
  const horizon = horizonDays ?? Math.min(60, Math.max(30, maxArrivalDay + 7))

  // 3/4. Simulação diária acumulando a ruptura por dia.
  const rupturaByDay = new Array<number>(horizon + 1).fill(0)
  let semDataCount = 0
  for (const r of rows) {
    if (HIDES_ZERADO.has(r.kanban_status)) continue // zerado por motivo operacional não é ruptura
    const daily = r.monthly_demand > 0 ? r.monthly_demand / CALENDAR_DAYS_PER_MONTH : 0

    // Chegadas por dia: inicia com os pedidos/plano; no modo replenish recebe re-pedidos.
    const arrivalsByDay = new Map<number, number>()
    let onOrder = 0
    for (const a of arrivalsByProduct.get(r.id) ?? []) {
      arrivalsByDay.set(a.day, (arrivalsByDay.get(a.day) ?? 0) + a.qty)
      if (a.day <= horizon) onOrder += a.qty
    }

    // Reposição contínua só vale p/ item reabastecível (tem ponto, lead e demanda).
    const reorderPoint = r.reorder_point ?? 0
    const lead = r.lead_time ?? null
    const reabastecivel = replenish && reorderPoint > 0 && lead != null && lead >= 0 && daily > 0

    let stock = r.current_stock
    const zeradoHoje = stock <= 0
    if (zeradoHoje) rupturaByDay[0]++
    // Em ruptura hoje, sem chegada datada, mas com pedido sem data → fora da curva.
    if (zeradoHoje && arrivalsByDay.size === 0 && semDataProducts.has(r.id)) semDataCount++

    for (let d = 1; d <= horizon; d++) {
      // Vendas perdidas (varejo): zerado não carrega saldo negativo — a venda só
      // consome o que existe. A chegada do dia entra como estoque disponível.
      let available = Math.max(0, stock)
      const chegou = arrivalsByDay.get(d)
      if (chegou) { available += chegou; onOrder -= chegou }
      stock = available - daily
      if (stock <= 0) rupturaByDay[d]++

      // Reposição contínua (política R,S): no ponto de ressuprimento e sem pedido em
      // trânsito → agenda um novo pedido (dobra se em ruptura) chegando após o lead.
      if (reabastecivel && onOrder <= 0 && stock <= reorderPoint) {
        const alvo = stock <= 0 ? reorderPoint * 2 : reorderPoint
        const qty = Math.ceil(Math.max(0, alvo - stock))
        if (qty > 0) {
          const dd = d + Math.max(1, lead)
          arrivalsByDay.set(dd, (arrivalsByDay.get(dd) ?? 0) + qty)
          onOrder += qty
        }
      }
    }
  }

  // 5. Série + métricas derivadas.
  const series: RupturaProjectionPoint[] = rupturaByDay.map((count, d) => ({
    date: new Date(today + d * DAY_MS).toISOString(),
    dayOffset: d,
    count,
    index: totalSkus > 0 ? count / totalSkus : 0,
  }))

  const currentIndex = series[0].index
  const rupturaTotal = series[0].count

  let minPoint = series[0]
  for (const p of series) if (p.count < minPoint.count) minPoint = p
  const endPoint = series[series.length - 1]
  const elimination = series.find(p => p.count === 0) ?? null

  // Gráfico: amostra semanal + último dia (rótulo DD/MM).
  const chartPoints: (RupturaProjectionPoint & { label: string })[] = []
  for (let d = 0; d <= horizon; d += 7) {
    const p = series[d]
    chartPoints.push({ ...p, label: fmtLabel(today + d * DAY_MS) })
  }
  if (chartPoints[chartPoints.length - 1].dayOffset !== horizon) {
    chartPoints.push({ ...endPoint, label: fmtLabel(today + horizon * DAY_MS) })
  }

  return {
    totalSkus,
    horizonDays: horizon,
    rupturaTotal,
    currentIndex,
    minIndex: minPoint.index,
    minIndexDate: minPoint.date,
    minCount: minPoint.count,
    endIndex: endPoint.index,
    endDate: endPoint.date,
    eliminationDate: elimination ? elimination.date : null,
    semDataCount,
    series,
    chartPoints,
  }
}
